import {
  Box,
  Button,
  Heading,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { useMemo, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeProvider';
import { useHistory } from '@/service/club/hooks';
import { numberToBRL } from '@/utils';
import { ProgressSale, SaleKind } from '@/service/club/types';

type ExtractFilter = 'all' | 'local' | 'online';

function saleKindLabel(kind?: SaleKind) {
  if (kind === 'online') return 'Cupom online';
  if (kind === 'local') return 'Estabelecimento';
  return 'Desconto';
}

function matchesFilter(sale: ProgressSale, filter: ExtractFilter) {
  if (filter === 'all') return sale.kind !== 'cinema';
  if (filter === 'online') return sale.kind === 'online';
  return sale.kind !== 'online' && sale.kind !== 'cinema';
}

export function EconometroList() {
  const { data: econometer, isLoading } = useHistory();
  const { colors } = useTheme();
  const [filter, setFilter] = useState<ExtractFilter>('all');

  const data = useMemo(() => {
    const sales =
      econometer?.progress?.reduce<ProgressSale[]>((acc, month) => {
        return acc.concat([...month.progress_sales].reverse());
      }, []) ?? [];

    return sales.filter((sale) => matchesFilter(sale, filter));
  }, [econometer, filter]);

  const filters: { id: ExtractFilter; label: string }[] = [
    { id: 'all', label: 'Tudo' },
    { id: 'local', label: 'Estabelecimentos' },
    { id: 'online', label: 'Cupons online' },
  ];

  return (
    <Box
      width="100%"
      backgroundColor="white"
      padding={{ base: '18px', md: '20px' }}
      borderRadius="24px"
      boxShadow="4px 4px 10px 0px #0000002E"
    >
      <VStack align="stretch" spacing={4}>
        <Heading color="#292927" fontSize="18px" fontFamily="Glory" fontWeight={500}>
          Extrato de economia
        </Heading>

        <Popover>
          <PopoverTrigger>
            <Button variant="link" height="auto" padding={0} _hover={{ textDecoration: 'none' }}>
              <HStack width="fit-content" align="center">
                <Text
                  textAlign="left"
                  color="#495057"
                  opacity="0.7"
                  fontSize="10pt"
                  textDecoration="underline"
                >
                  Saiba como é calculada sua economia.
                </Text>
                <IoMdInformationCircleOutline color="#495057" size={20} />
              </HStack>
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader>Cálculo de Economia</PopoverHeader>
            <PopoverBody fontSize="10pt">
              <Text textAlign="left" paddingBottom="10px">
                Sua economia de cupons é calculada de 3 formas:
              </Text>
              <VStack align="stretch" spacing={2} fontSize="10pt">
                <Text>1. Pelo valor informado do estabelecimento parceiro quando você utilizou o desconto.</Text>
                <Text>2. Pelo valor que você informa (se sobrepõe ao valor dele).</Text>
                <Text>3. Se nenhum dos dois informou o valor, usamos a economia média do estabelecimento.</Text>
              </VStack>
              <Text textAlign="left" paddingTop="10px">
                Compras físicas, como ingressos de cinema, ficam no histórico de compras e não entram neste extrato.
              </Text>
            </PopoverBody>
          </PopoverContent>
        </Popover>

        <HStack spacing={2} flexWrap="wrap">
          {filters.map((item) => {
            const selected = filter === item.id;
            return (
              <Button
                key={item.id}
                size="sm"
                borderRadius="full"
                fontFamily="Glory"
                fontWeight={500}
                onClick={() => setFilter(item.id)}
                bg={selected ? colors.primaryColor : '#F4F6F7'}
                color={selected ? 'white' : '#495057'}
                _hover={{ bg: selected ? colors.primaryColor : '#E8ECED' }}
              >
                {item.label}
              </Button>
            );
          })}
        </HStack>

        {isLoading && (
          <HStack justify="center" py={8}>
            <Spinner color={colors.primaryColor} />
          </HStack>
        )}

        {!isLoading && data.length === 0 && (
          <Text textAlign="center" color="#747474" fontFamily="Glory" py={6}>
            Nenhuma economia de cupom neste filtro.
          </Text>
        )}

        {!isLoading &&
          data.map((sale) => (
            <HStack
              key={sale.id ?? `${sale.company}-${sale.created_at}`}
              justify="space-between"
              align="flex-start"
              py={3}
              borderBottom="1px solid #EEF1F2"
            >
              <Box>
                <Text fontFamily="Glory" fontWeight={500} color="#292927">
                  {sale.company || sale.discount || 'Desconto'}
                </Text>
                <Text fontSize="12px" color="#747474">
                  {saleKindLabel(sale.kind)} · {new Date(sale.created_at).toLocaleDateString('pt-BR')}
                </Text>
              </Box>
              <Text fontFamily="Glory" fontWeight={600} color={colors.primaryColor} whiteSpace="nowrap">
                {numberToBRL(Number(sale.saved) || 0)}
              </Text>
            </HStack>
          ))}
      </VStack>
    </Box>
  );
}
